import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Calendar, Clock, DollarSign } from "lucide-react";
import { formatCurrency, formatDate, daysUntil, getPacing } from "./goalFormat";

const MoneyInput = ({ label, value, onChange, testId, className }) => (
  <div>
    <label className="text-sm text-gray-400 mb-1 block">{label}</label>
    <div className="relative">
      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
      <Input type="number" value={value} onChange={(e) => onChange(e.target.value)} className={`pl-10 bg-white/5 border-gray-700 text-white w-64 ${className}`} data-testid={testId} />
    </div>
  </div>
);

const DeadlineBox = ({ goalEndDate, remaining, isEditing, onChangeDate }) => {
  const days = daysUntil(goalEndDate);
  const urgent = days !== null && days < 30;
  const dayColor = urgent ? "text-red-400" : days < 90 ? "text-yellow-400" : "text-ism-teal";
  return (
    <div className={`mb-8 p-4 rounded-lg border ${urgent ? "bg-red-500/10 border-red-500/30" : "bg-white/5 border-gray-700"}`} data-testid="goal-deadline-box">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Calendar className={`h-5 w-5 ${urgent ? "text-red-400" : "text-ism-teal"}`} />
          <div>
            <p className="text-sm text-gray-400">Goal Completion Date</p>
            {isEditing ? (
              <Input type="date" value={goalEndDate} onChange={(e) => onChangeDate(e.target.value)} className="bg-white/5 border-gray-700 text-white mt-1 w-48" data-testid="goal-end-date-input" />
            ) : (
              <p className="text-lg font-normal text-white" data-testid="goal-end-date">{formatDate(goalEndDate)}</p>
            )}
          </div>
        </div>
        {days !== null && !isEditing && (
          <div className="text-right">
            <div className={`flex items-center gap-2 ${dayColor}`}>
              <Clock className="h-4 w-4" />
              <span className="text-2xl font-normal" data-testid="days-remaining">{days}</span>
            </div>
            <p className="text-sm text-gray-400">days remaining</p>
          </div>
        )}
      </div>
      {days !== null && days > 0 && !isEditing && (
        <div className="mt-3 pt-3 border-t border-gray-700">
          <p className="text-sm text-gray-400">
            Required run rate: <span className="text-white font-normal">${((remaining / days) * 30 / 1000).toFixed(0)}K/month</span> to hit goal
          </p>
        </div>
      )}
    </div>
  );
};

export const RevenueProgressCard = ({ data, pipelineValue, isEditing, editData, setEditData }) => {
  const { revenueGoal, currentRevenue } = data;
  const progressPercentage = revenueGoal > 0 ? (currentRevenue / revenueGoal) * 100 : 0;
  const remaining = revenueGoal - currentRevenue;
  const pacing = getPacing(progressPercentage);
  const PacingIcon = pacing.icon;
  const patch = (key) => (value) => setEditData((prev) => ({ ...prev, [key]: value }));

  return (
    <Card className="glass-card border-gray-800/50 p-8" data-testid="revenue-progress-card">
      <div className="flex items-start justify-between mb-8 gap-4">
        {isEditing ? (
          <div className="space-y-4">
            <MoneyInput label="Revenue Goal" value={editData.revenueGoal} onChange={patch("revenueGoal")} testId="revenue-goal-input" className="text-2xl font-normal" />
            <MoneyInput label="Current Revenue" value={editData.currentRevenue} onChange={patch("currentRevenue")} testId="current-revenue-input" className="text-xl" />
          </div>
        ) : (
          <div>
            <h2 className="text-2xl font-normal text-white mb-2" data-testid="revenue-goal-value">{formatCurrency(revenueGoal)}</h2>
            <p className="text-gray-400">Revenue Goal</p>
          </div>
        )}
        <Badge className={`${pacing.color} border-0 bg-white/10 text-lg px-4 py-2 flex items-center gap-2`} data-testid="pacing-badge">
          <PacingIcon className="h-5 w-5" />
          {pacing.status}
        </Badge>
      </div>

      <DeadlineBox goalEndDate={isEditing ? editData.goalEndDate : data.goalEndDate} remaining={remaining} isEditing={isEditing} onChangeDate={patch("goalEndDate")} />

      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-400">Current Progress</span>
          <span className="text-lg font-normal text-white" data-testid="progress-percentage">{Math.round(progressPercentage)}%</span>
        </div>
        <div className="w-full bg-gray-800 rounded-full h-6 overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${Math.min(100, progressPercentage)}%` }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="h-full bg-gradient-to-r from-ism-teal to-[#008570] flex items-center justify-end px-2"
          >
            {progressPercentage > 10 && <span className="text-xs font-normal text-white">{formatCurrency(currentRevenue)}</span>}
          </motion.div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <div>
          <p className="text-sm text-gray-400 mb-1">Current Revenue</p>
          <p className="text-2xl font-normal text-white">{formatCurrency(currentRevenue)}</p>
        </div>
        <div>
          <p className="text-sm text-gray-400 mb-1">Remaining to Goal</p>
          <p className="text-2xl font-normal text-ism-teal">{formatCurrency(remaining)}</p>
        </div>
        <div>
          <p className="text-sm text-gray-400 mb-1">Pipeline Value</p>
          <p className="text-2xl font-normal text-yellow-400">{formatCurrency(pipelineValue || 0)}</p>
        </div>
      </div>
    </Card>
  );
};
